/**
 * API client for memory node detail and review,
 * conversation summaries, and memory settings.
 *
 * All endpoints return { status: "success", data: T } on success.
 */

import { apiRequest } from "./client";
import {
  normalizeReviewItem,
  type MemoryReviewWire,
} from "./memory-review-normalize";

interface ApiResponse<T> {
  status: "success";
  data: T;
}

/* ---------- Shared types ---------- */

export type KnowledgeStatus = "proposed" | "active" | "rejected" | "retired";
export type KnowledgeOrigin = "explicit" | "inferred" | "curated";

/* ---------- Node JSON (nodes/), the fields the UI reads ---------- */

export interface ReviewNotes {
  conflicts: string[];
  possible_duplicates: string[];
}

export interface KnowledgeEvidence {
  id: string;
  source_kind: string;
  source_id: string;
  stance: string;
  excerpt: string;
  source_available: boolean;
  observation_id: string;
  source_message_id: string | null;
  source_task_id: string | null;
  source_tool_call_id: string | null;
  extractor_version: string;
  policy_version: string;
  observed_at: string;
}

export interface NodeEdge {
  id: string;
  relationship_type: string;
  source: string;
  target: string;
  target_name: string;
  source_name?: string;
  claim?: string;
  confidence?: number;
  evidence?: KnowledgeEvidence[];
}

/** A proposal waiting for review; `snapshot.payload` is the suggested content. */
export interface PendingRevision {
  id: string;
  operation: string;
  snapshot: { payload: Record<string, unknown> };
  review_notes?: ReviewNotes;
  created_at: string;
}

export interface KnowledgeNodeRecord {
  id: string;
  node_type: string;
  name: string;
  description: string;
  /** Kind payload, validated by the backend registry. */
  metadata: Record<string, unknown>;
  status: KnowledgeStatus;
  version: number;
  origin: KnowledgeOrigin;
  /** True for the shared curated catalog (activity, library process). */
  shared: boolean;
  invalid_at: string | null;
  created_at: string;
  updated_at: string;
  pending_revision: PendingRevision | null;
  review_notes?: ReviewNotes;
  /** List items: outgoing structural edges. Detail: every live edge touching the node. */
  edges: NodeEdge[];
}

/** A list item: the newest live fact's claim, `null` for a thing without facts. */
export interface KnowledgeNodeListItem extends KnowledgeNodeRecord {
  latest_claim: string | null;
}

/** One user-process step after `retrieval_rendering.apply_overlay`. */
export interface EffectiveStep {
  step_id: string;
  text: string;
}

export interface KnowledgeNodeDetail extends KnowledgeNodeRecord {
  /** Present only on user `process` detail. */
  effective_steps?: EffectiveStep[];
  evidence?: KnowledgeEvidence[];
}

export interface MemoryProvenanceSource {
  id: string;
  kind: string;
  available: boolean;
  redacted: boolean;
  deleted: boolean;
  message_id?: string;
  conversation_id?: string;
  execution_id?: string | null;
  operation_id?: string;
  task_id?: string | null;
  effect_state?: string;
  verification_id?: string;
  criterion_id?: string | null;
  verification_status?: string;
  outcome_state?: string;
  conversation_close_id?: string;
}

export interface MemoryProvenanceItem {
  evidence_id: string;
  stance: string;
  excerpt: string;
  observed_at: string;
  observation: MemoryProvenanceSource;
}

export interface MemoryProvenancePage {
  node_id: string;
  items: MemoryProvenanceItem[];
  pagination: { has_more: boolean; next_cursor: string | null };
}

/** A policy-backed item returned by the unified review queue. */
export interface MemoryReviewItem {
  id: string;
  node_id: string | null;
  item_type: string;
  status: "pending" | "applied" | "rejected" | "expired";
  title: string;
  reason_code: string;
  reason_label: string;
  confidence: number | null;
  current_value: string | null;
  proposed_value: Record<string, unknown>;
  source_node_id: string | null;
  target_node_id: string | null;
  attributed: boolean;
  source_quote: string;
  undo_available: boolean;
  created_at: string;
}

export interface MemoryReviewPage {
  total?: number;
  items: MemoryReviewItem[];
  pagination: { has_more: boolean; next_cursor: string | null };
}

/** Revise semantics: metadata keys merge (null removes a key); clear_edge_types removes every edge of a type. */
export interface NodeUpdateInput {
  name?: string;
  description?: string;
  metadata?: Record<string, unknown>;
  clear_edge_types?: string[];
}

export interface ConversationSummary {
  id: string;
  conversation_id: string | null;
  conversation_title: string;
  entry_type: string;
  project: string | null;
  title: string;
  content: string;
  source_execution_id: string | null;
  created_at: string;
}

export function withQuery(
  path: string,
  params: Record<string, string | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, value);
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

/* ---------- Nodes ---------- */

export async function getNodeDetail(
  nodeId: string,
): Promise<KnowledgeNodeDetail> {
  const res = await apiRequest<ApiResponse<KnowledgeNodeDetail>>(
    `/api/v1/memory/nodes/${nodeId}/`,
  );
  return res.data;
}

/**
 * The things worth a "Denker learned" wall card: the user's own active
 * things (never the shared catalog) with a live fact or a description, not
 * hidden from the wall unless they learned a fact since, newest created
 * first. With `createdAfter` (ISO time) only those created after it, and at
 * most `limit` (1–200, the server's 200 by default). With
 * `learnedInProject` only what that project's runs taught Denker, each
 * saying the newest claim learned there. Silent: the wall shows them in the
 * background.
 */
export async function listWallThings(
  options: {
    createdAfter?: string;
    limit?: number;
    learnedInProject?: string;
  } = {},
): Promise<KnowledgeNodeListItem[]> {
  const res = await apiRequest<ApiResponse<KnowledgeNodeListItem[]>>(
    withQuery("/api/v1/memory/nodes/", {
      status: "active",
      order: "created",
      on_wall: "true",
      created_after: options.createdAfter,
      limit: options.limit?.toString(),
      learned_in_project: options.learnedInProject,
    }),
    { silent: true },
  );
  return res.data;
}

/** Hide: the thing's wall card goes on every device until it learns a new fact; the memory stays. */
export async function hideThingFromWall(nodeId: string): Promise<void> {
  await apiRequest(`/api/v1/memory/nodes/${nodeId}/wall-hide/`, {
    method: "POST",
  });
}

/** Forget: deletes the thing with its facts. */
export async function forgetThing(nodeId: string): Promise<void> {
  await apiRequest(`/api/v1/memory/nodes/${nodeId}/`, { method: "DELETE" });
}

export async function getNodeProvenance(
  nodeId: string,
  options?: { cursor?: string; limit?: number },
): Promise<MemoryProvenancePage> {
  const res = await apiRequest<ApiResponse<MemoryProvenancePage>>(
    withQuery(`/api/v1/memory/nodes/${nodeId}/provenance/`, {
      cursor: options?.cursor,
      limit: options?.limit?.toString(),
    }),
  );
  return res.data;
}

export async function listMemoryReview(options?: {
  state?: "pending" | "recent";
  cursor?: string;
  limit?: number;
}): Promise<MemoryReviewPage> {
  const res = await apiRequest<
    ApiResponse<MemoryReviewWire[]> & {
      total?: number;
      pagination?: MemoryReviewPage["pagination"];
    }
  >(
    withQuery("/api/v1/memory/review/", {
      state: options?.state,
      cursor: options?.cursor,
      limit: options?.limit?.toString(),
    }),
    { silent: true },
  );
  return {
    items: res.data.map(normalizeReviewItem),
    total: res.total,
    pagination: res.pagination ?? { has_more: false, next_cursor: null },
  };
}

export async function decideMemoryReview(
  reviewId: string,
  decision: "accept" | "reject" | "restrict" | "undo" | "forget",
  appliesTo?: string,
): Promise<MemoryReviewItem> {
  const res = await apiRequest<ApiResponse<{ candidate: MemoryReviewWire }>>(
    `/api/v1/memory/review/${reviewId}/decision/`,
    {
      method: "POST",
      body: {
        action: decision,
        ...(appliesTo ? { applies_to: appliesTo } : {}),
      },
    },
  );
  return normalizeReviewItem(res.data.candidate);
}

export async function updateNode(
  nodeId: string,
  input: NodeUpdateInput & { expected_version: number },
): Promise<KnowledgeNodeDetail> {
  const res = await apiRequest<ApiResponse<KnowledgeNodeDetail>>(
    `/api/v1/memory/nodes/${nodeId}/`,
    { method: "PATCH", body: input },
  );
  return res.data;
}

/* ---------- Conversation summaries ---------- */

export async function listConversationSummaries(params: {
  days: number;
  limit: number;
}): Promise<ConversationSummary[]> {
  const res = await apiRequest<ApiResponse<ConversationSummary[]>>(
    withQuery("/api/v1/memory/conversation-summaries/", {
      days: params.days.toString(),
      limit: params.limit.toString(),
    }),
  );
  return res.data;
}

/* ---------- Capture health ---------- */

export interface MemoryCaptureHealth {
  capture_enabled: boolean;
  retrieval_enabled: boolean;
  last_success_at: string | null;
  failed_count: number;
  pending_count: number;
  failures: { id: string; kind: string; error: string; failed_at: string }[];
}

export interface MemoryNodeActivity {
  deliveries: {
    execution_id: string;
    task_id: string | null;
    task_number: number | null;
    task_title: string | null;
    reason: string;
    delivered_at: string;
  }[];
  revisions: {
    state: string;
    operation: string;
    actor_kind: string;
    created_at: string;
  }[];
}

export async function getCaptureHealth(): Promise<MemoryCaptureHealth> {
  const res = await apiRequest<ApiResponse<MemoryCaptureHealth>>(
    "/api/v1/memory/capture-health/",
    { silent: true },
  );
  return res.data;
}

export async function retryFailedCaptures(): Promise<number> {
  const res = await apiRequest<ApiResponse<{ retried: number }>>(
    "/api/v1/memory/capture-jobs/retry/",
    { method: "POST", body: {} },
  );
  return res.data.retried;
}

export async function getNodeActivity(
  nodeId: string,
): Promise<MemoryNodeActivity> {
  const res = await apiRequest<ApiResponse<MemoryNodeActivity>>(
    `/api/v1/memory/nodes/${nodeId}/activity/`,
  );
  return res.data;
}

/* ---------- Memory Settings ---------- */

export interface MemorySettings {
  memory_runner: string;
  memory_model: string;
  memory_graph_enabled: boolean;
}

export async function getMemorySettings(): Promise<MemorySettings> {
  const res = await apiRequest<ApiResponse<MemorySettings>>(
    "/api/v1/memory/settings/",
  );
  return res.data;
}

export async function updateMemorySettings(
  settings: Partial<MemorySettings>,
): Promise<MemorySettings> {
  const res = await apiRequest<ApiResponse<MemorySettings>>(
    "/api/v1/memory/settings/",
    {
      method: "PUT",
      body: settings,
    },
  );
  return res.data;
}
